import React from "react";
import { Save } from "lucide-react";
import { Button } from "../../ui/button";
import { NumberInput } from "../../ui/number-input";

/**
 * Footer row for a custom / one-off line. The labels start with the estimator's
 * intent (purchase it or build it), while preserving the existing component
 * types the pricing engine expects.
 *
 * Rendered as a single full-width cell via colSpan={COLUMN_COUNT} so it
 * sits flush inside the table without disturbing column geometry.
 */
export default function CustomFooterRow({
  lineItem,
  lineIndex,
  dispatch,
  onSaveAsTemplate,
  columnCount = 9,
}) {
  const addComponent = (componentType) => {
    dispatch({ type: "ADD_COMPONENT", index: lineIndex, componentType });
  };

  return (
    <tr className="border-b border-amber-500/20 bg-amber-500/[0.03] shadow-[inset_3px_0_0_0_theme(colors.custom.DEFAULT)]">
      <td colSpan={columnCount} className="py-3 px-5">
        <div className="flex flex-col gap-3">
          {/* Add component buttons */}
          <div className="flex gap-2 flex-wrap items-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addComponent("material")}
              className="text-[11px] border-dashed border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
            >
              + Build material
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addComponent("operation")}
              className="text-[11px] border-dashed border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
            >
              + Shop operation
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addComponent("hardware")}
              className="text-[11px] border-dashed border-amber-500/40 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
            >
              + Purchased item
            </Button>
          </div>

          {/* Manual override escape hatch */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-estimate-muted">
              Advanced: override line cost
            </span>
            <NumberInput
              align="right"
              step="0.01"
              placeholder="$ manual"
              value={lineItem.manual_total_override ?? ""}
              onValueChange={(v) =>
                dispatch({
                  type: "UPDATE_LINE_ITEM_FIELD",
                  index: lineIndex,
                  field: "manual_total_override",
                  value: v,
                })
              }
              className="h-8 w-[120px] text-[12px] border-dashed"
            />
          </div>

          {/* Save as Template */}
          <div className="flex items-center gap-2 pt-2 border-t border-estimate-border/40">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onSaveAsTemplate && onSaveAsTemplate(lineIndex)}
              className="text-[11px]"
            >
              <Save className="h-3.5 w-3.5" />
              Save as Product Template
            </Button>
            <span className="text-[10px] text-estimate-muted">
              Reuse this composition on future estimates
            </span>
          </div>
        </div>
      </td>
    </tr>
  );
}
